import React, { useCallback, useEffect, useState } from 'react';
import {
    TabContent,
    TabPane,
    Nav,
    NavItem,
    NavLink,
    Row,
    Col,
    Container,
} from 'reactstrap';
import { Helmet } from "react-helmet";
import classnames from 'classnames';
import "react-datepicker/dist/react-datepicker.css";
import CardSecondary from '../../components/card/card-secondary';
import Main from '../../layout/main';
import ProjectsLoader from "../../components/projects/projectsSkeleton";
import API from '../../API';
import axios from 'axios';

const TABS = [
    { id: '1', key: 'leads', title: 'Leads', emptyMessage: 'Projects will appear here once you purchase a lead.' },
    { id: '2', key: 'bidding', title: 'Bidding', emptyMessage: 'Projects will appear here once you place a bid on them.' },
    { id: '3', key: 'hired', title: 'Hired', emptyMessage: 'Projects will appear here when you\'re hired by customers.' },
    { id: '4', key: 'bookmarks', title: 'Bookmarks', emptyMessage: 'Your bookmarked projects will appear here.' },
    { id: '5', key: 'archived', title: 'Archived', emptyMessage: 'Your archived projects will appear here.' },
];

const normalizeProjects = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.projects)) return payload.projects;
    return [];
};

const MyActivities = () => {
    const [activeTab, setActiveTab] = useState('1');
    const [loading, setLoading] = useState(true);
    const [activities, setActivities] = useState({
        leads: [],
        bidding: [],
        hired: [],
        bookmarks: [],
        archived: [],
    });

    const loadActivities = useCallback(async () => {
        setLoading(true);
        try {
            const [
                leadsResponse,
                biddingResponse,
                hiredResponse,
                bookmarksResponse,
                archivedResponse,
            ] = await Promise.all([
                axios.get(`${API.baseURL}/v2/providers/purchased_lead_projects`, API.options),
                axios.get(`${API.baseURL}/v2/providers/activities/in_bidding`, API.options),
                axios.get(`${API.baseURL}/v2/providers/my_projects/in_processing`, API.options),
                axios.get(`${API.baseURL}/v2/providers/activities/bookmark`, API.options),
                axios.get(`${API.baseURL}/v2/providers/hidden_leads`, API.options),
            ]);

            setActivities({
                leads: normalizeProjects(leadsResponse?.data?.data),
                bidding: normalizeProjects(biddingResponse?.data?.data),
                hired: normalizeProjects(hiredResponse?.data?.data),
                bookmarks: normalizeProjects(bookmarksResponse?.data?.data),
                archived: normalizeProjects(archivedResponse?.data?.data),
            });
        } catch (error) {
            setActivities({
                leads: [],
                bidding: [],
                hired: [],
                bookmarks: [],
                archived: [],
            });
        } finally {
            setLoading(false);
        }
    }, []);

    const toggle = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    useEffect(() => {
        loadActivities();
    }, [loadActivities]);

    const renderEmpty = (message) => (
        <Row>
            <Col sm="12" className="mt-4">
                <div className="ebw-section__empty position-relative">
                    <div className="ebw-section__empty-imgs w-100">
                        <img className="w-100 ebw-section__empty-background" src="/images/projects/project-empty-background.svg" alt="" />
                    </div>
                    <div className="ebw-section__empty-content d-flex align-items-end position-absolute">
                        <div className="ebw-section__empty-title h-100 d-flex align-items-center ebw-title__primary--white">{message}</div>
                        <div className="ebw-section__empty-img"><img className="w-100" src="/images/projects/project-empty-img.png" alt="" /></div>
                    </div>
                </div>
            </Col>
        </Row>
    );

    const renderProjects = (projects, options = {}) => {
        if (!projects || projects.length === 0) return null;

        return (
            <Row>
                {projects.map((project, index) => (
                    <Col key={project.id || index} sm="6" lg="4" className="mt-4">
                        <CardSecondary
                            projectName={project.name}
                            bidAmount={project.bid_amount}
                            bidtype={project.bid_type}
                            projectEstimationHour={project.hours}
                            projectImage={project.project_image_url}
                            projectDate={project.created_at}
                            projectCategory={project.project_type}
                            projectLocation={project.city}
                            projectLinks={`/project-detail/${project.id}/activities`}
                            project_post_type={project.project_post_type}
                            status={project.status}
                            project_status={project.status}
                            lead_purchased={project.lead_purchased}
                            bookmark={project.bookmarked_by_current_provider}
                            project_id={project.id}
                            noShowLeadCard={options.noShowLeadCard}
                            onUnarchive={options.onUnarchive}
                        />
                    </Col>
                ))}
            </Row>
        );
    };

    return (
        <div>
            <Helmet>
                <meta charSet="utf-8" />
                <meta name="description" content="Browse all your provider activities including leads, bidding, hired, bookmarks and archived projects." />
                <title>My Activities - Easy Bids Pro</title>
                <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css"></link>
            </Helmet>
            <Main additionalClass_main="myprojects">
                <Nav tabs className="ebw-projects__navtab">
                    <Container>
                        <Row>
                            {TABS.map((tab) => (
                                <NavItem className="ebw-tab__item" key={tab.id}>
                                    <NavLink
                                        className={classnames({ active: activeTab === tab.id })}
                                        onClick={() => { toggle(tab.id); }}
                                    >
                                        {tab.title}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Row>
                    </Container>
                </Nav>

                <section className="ebw-section ebw-section__pages">
                    <TabContent activeTab={activeTab}>
                        {TABS.map((tab) => {
                            const projects = activities[tab.key] || [];
                            const paneOptions = {
                                ...(tab.key === 'bidding' ? { noShowLeadCard: true } : {}),
                                ...(tab.key === 'archived' ? { onUnarchive: loadActivities } : {}),
                            };

                            return (
                                <TabPane tabId={tab.id} key={tab.id}>
                                    <Container>
                                        <div className="d-md-flex justify-content-between">
                                            <div className="ebw-title__primary">{tab.title}</div>
                                        </div>

                                        {loading ? (
                                            <div className="ebw-skeleton__loader">
                                                <ProjectsLoader count={9} />
                                            </div>
                                        ) : (
                                            <>
                                                {projects.length === 0 && renderEmpty(tab.emptyMessage)}
                                                {renderProjects(projects, paneOptions)}
                                            </>
                                        )}
                                    </Container>
                                </TabPane>
                            );
                        })}
                    </TabContent>
                </section>
            </Main>
        </div>
    );
};

export default MyActivities;
